/**
 * Password Gate Module
 * Simple email/password check before Supabase authentication
 */

const GATE_CONFIG = {
  email: 'ets.coding2020@gmail.com',
  password: '123456789@',
  maxAttempts: 3,
  lockoutMinutes: 10,
};

const STORAGE_KEYS = {
  passed: 'chorus_gate_passed',
  attempts: 'chorus_gate_attempts',
  lockedUntil: 'chorus_gate_locked_until',
};

/**
 * Check if the password gate has been passed
 */
export function isGatePassed() {
  if (GATE_CONFIG.email === '' && GATE_CONFIG.password === '') {
    // No gate configured, pass through
    return true;
  }
  
  try {
    const passed = localStorage.getItem(STORAGE_KEYS.passed);
    return passed === 'true';
  } catch {
    return false;
  }
}

/**
 * Check if currently locked out
 */
export function isLockedOut() {
  try {
    const lockedUntil = localStorage.getItem(STORAGE_KEYS.lockedUntil);
    if (!lockedUntil) return false;
    
    const lockoutTime = parseInt(lockedUntil, 10);
    const now = Date.now();
    
    if (now >= lockoutTime) {
      // Lockout expired, clear it
      clearGateState();
      return false;
    }
    
    return true;
  } catch {
    return false;
  }
}

/**
 * Get remaining lockout time in seconds
 */
export function getLockoutTimeRemaining() {
  try {
    const lockedUntil = localStorage.getItem(STORAGE_KEYS.lockedUntil);
    if (!lockedUntil) return 0;
    
    const lockoutTime = parseInt(lockedUntil, 10);
    const remaining = Math.ceil((lockoutTime - Date.now()) / 1000);
    
    return remaining > 0 ? remaining : 0;
  } catch {
    return 0;
  }
}

/**
 * Get remaining attempts
 */
export function getRemainingAttempts() {
  try {
    const attempts = parseInt(localStorage.getItem(STORAGE_KEYS.attempts) || '0', 10);
    return Math.max(0, GATE_CONFIG.maxAttempts - attempts);
  } catch {
    return GATE_CONFIG.maxAttempts;
  }
}

/**
 * Validate email and password
 */
export function validate(email, password) {
  if (isLockedOut()) {
    return { success: false, error: 'locked_out', remaining: 0 };
  }
  
  if (email === GATE_CONFIG.email && password === GATE_CONFIG.password) {
    // Success - mark gate as passed
    try {
      localStorage.setItem(STORAGE_KEYS.passed, 'true');
      localStorage.removeItem(STORAGE_KEYS.attempts);
      localStorage.removeItem(STORAGE_KEYS.lockedUntil);
    } catch (e) {
      console.error('Failed to save gate state:', e);
    }
    return { success: true };
  }
  
  // Failed - record attempt
  recordFailedAttempt();
  
  const remaining = getRemainingAttempts();
  return { 
    success: false, 
    error: 'invalid_credentials',
    remaining 
  };
}

/**
 * Record a failed attempt
 */
function recordFailedAttempt() {
  try {
    const attempts = parseInt(localStorage.getItem(STORAGE_KEYS.attempts) || '0', 10) + 1;
    localStorage.setItem(STORAGE_KEYS.attempts, String(attempts));
    
    if (attempts >= GATE_CONFIG.maxAttempts) {
      // Lock out
      const lockoutTime = Date.now() + (GATE_CONFIG.lockoutMinutes * 60 * 1000);
      localStorage.setItem(STORAGE_KEYS.lockedUntil, String(lockoutTime));
    }
  } catch (e) {
    console.error('Failed to record attempt:', e);
  }
}

/**
 * Clear all gate state (for testing or reset)
 */
export function clearGateState() {
  try {
    localStorage.removeItem(STORAGE_KEYS.passed);
    localStorage.removeItem(STORAGE_KEYS.attempts);
    localStorage.removeItem(STORAGE_KEYS.lockedUntil);
  } catch (e) {
    console.error('Failed to clear gate state:', e);
  }
}

/**
 * Reset gate state (alias for clearGateState for convenience)
 */
export function resetGate() {
  clearGateState();
}
